// src/components/FeedBackCard.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { sanitizeHtml, encodeEmailBrackets } from '../utils/sanitize';

export default function FeedBackCard({ email, receiverEmail, onClose, t: labels }) {
  const { t: _t, i18n } = useTranslation('game');

  const lang = ((i18n?.language) || localStorage.getItem('i18nextLng') || 'en')
    .slice(0, 2)
    .toLowerCase();

  // If email has bilingual blocks, pick the right one; otherwise treat email as already flattened
  const block = (email?.en || email?.nl)
    ? (email[lang] || email.en || email.nl || {})
    : email || {};

  const cleanBody = sanitizeHtml(block.content ?? block.body ?? '');
  const cleanFooter = sanitizeHtml(block.footer ?? '');

  const cleanSender = sanitizeHtml(encodeEmailBrackets(block.sender || ''));

  const stopAnchorNav = (e) => {
    const t = e.target;
    if (t && t.tagName === 'A') e.preventDefault();
  };

  // Labels (prefer props from Home, then i18n fallbacks)
  const title   = labels?.phishingReasonTitle || _t('feedback.title', 'Oops! This was a phishing email.');
  const message = labels?.phishingReasonDesc  || _t('feedback.desc', 'Be cautious and always verify suspicious links or senders.');
  const cta     = labels?.continueButton      || _t('buttons.continue', 'Continue');

  return (
    <div className="relative max-w-[680px] w-full sm:w-[90%] md:w-[80%] lg:w-[680px] rounded-2xl shadow-xl bg-[#f6fafc] p-6 text-[#2b3b4b]">
      {/* Training UI header (outside email) */}
      <div className="bg-red-100 border border-red-300 text-red-800 p-4 mb-5 rounded-lg text-sm font-medium">
        <strong>{title}</strong>
        <br />
        {message}
      </div>

      {/* Email preview card (clearly separated) */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-md overflow-hidden">
        <div className="p-5">
          <h3 className="pb-4 text-xl font-semibold break-words">{block.subject || ''}</h3>

          <div className="pb-4 text-sm text-gray-600 space-y-1">
            <div className="flex flex-wrap items-center">
              <strong className="mr-1">From:</strong>
              {block.department ? <span className="mr-2">{block.department}</span> : null}
              <span
                className="text-gray-500 break-words"
                dangerouslySetInnerHTML={{ __html: cleanSender }}
              />
            </div>
            <p><strong>To:</strong> {receiverEmail ?? 'You'}</p>
          </div>

          <hr className="border-gray-200" />

          <div
            className="py-4 text-sm leading-relaxed break-words"
            onClick={stopAnchorNav}
            dangerouslySetInnerHTML={{ __html: cleanBody }}
          />

          {(block.footer || '').trim() && (
            <>
              <hr className="border-gray-200" />
              <div
                className="text-xs text-gray-500 text-center py-2 break-words"
                onClick={stopAnchorNav}
                dangerouslySetInnerHTML={{ __html: cleanFooter }}
              />
            </>
          )}
        </div>
      </div>

      {/* CTA footer (clearly not part of the email) */}
      <div className="pt-5 text-center">
        <button
          onClick={onClose}
          className="group inline-flex items-center justify-center gap-2 rounded-2xl px-7 py-3.5
                     bg-[#00aca9] text-white font-semibold
                     shadow-[0_6px_20px_rgba(0,172,169,0.25)]
                     transition-all duration-200
                     hover:shadow-[0_10px_28px_rgba(0,172,169,0.35)] hover:-translate-y-0.5
                     active:translate-y-0
                     focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00aca9]/40"
        >
          {cta}
          <svg className="size-4 transition-transform duration-200 group-hover:translate-x-0.5" viewBox="0 0 20 20" fill="currentColor"><path d="M7.293 16.293a1 1 0 0 1 0-1.414L11.172 11H3a1 1 0 1 1 0-2h8.172L7.293 5.121A1 1 0 1 1 8.707 3.707l6 6a1 1 0 0 1 0 1.414l-6 6a1 1 0 0 1-1.414 0z"/></svg>
        </button>
      </div>
    </div>
  );
}